import { useCallback, useEffect, useReducer, useRef } from "react";
import { getCamera, getCaptureDevice, getStoreTree, getView, postChangeset } from "./captureDeviceService";
import buildChangeset from "./changeset";
import { initialState, treeReducer } from "../TreePrototype/treeReducer";

/**
 * Fetch the Store → CaptureDevice → Camera → View hierarchy and keep it in a
 * local reducer that supports add/update/delete/duplicate, then push the diff
 * to the bulk changeset endpoint when the user clicks Save All.
 *
 * Refreshing the page (or calling reset()) re-fetches and drops local drafts.
 */
export default function useStoreCameraTree(storeId) {
  const [state, dispatch] = useReducer(treeReducer, initialState);

  // Tracks the most recent in-flight load so older fetches that resolve late
  // (e.g. user switched store quickly) can be discarded instead of clobbering
  // the newer tree.
  const loadTokenRef = useRef({ cancelled: false });

  const load = useCallback(async () => {
    // Mark any previous in-flight load as cancelled; new requests use a fresh token.
    loadTokenRef.current.cancelled = true;
    const myToken = { cancelled: false };
    loadTokenRef.current = myToken;

    if (!storeId) {
      if (!myToken.cancelled) dispatch({ type: "LOAD_OK", tree: [] });
      return;
    }
    dispatch({ type: "LOAD_START" });

    try {
      // Single aggregated GET — the backend returns the full
      // device → cameras → views hierarchy in one round-trip (3 SQL queries),
      // with stable ordering hardcoded server-side. Replaces the prior 1+N+M
      // cascade that drove ~85 round-trips on a mid-size store.
      const tree = await getStoreTree(storeId);
      if (myToken.cancelled) return;
      dispatch({ type: "LOAD_OK", tree });
    } catch (error) {
      if (myToken.cancelled) return;
      dispatch({ type: "LOAD_ERROR", error });
    }
  }, [storeId]);

  useEffect(() => {
    load();
    return () => {
      loadTokenRef.current.cancelled = true;
    };
  }, [load]);

  const refetchEntity = useCallback((publicId) => {
    if (publicId.startsWith("cd_")) return getCaptureDevice(publicId);
    if (publicId.startsWith("cam_")) return getCamera(publicId);
    if (publicId.startsWith("view_")) return getView(publicId);
    return Promise.resolve(null);
  }, []);

  const saveAll = useCallback(async () => {
    // Snapshot the tree we're about to save; the reducer uses it to compute the
    // new server baseline so in-flight edits made between SAVE_START and SAVE_OK
    // remain flagged as "modified" instead of being silently absorbed as pristine.
    const sentTree = state.tree;
    // Capture the load token so any subsequent reload (Discard, store switch) can
    // invalidate this save cycle's late-arriving dispatches (SAVE_OK, STALE_REFRESH,
    // SAVE_ERROR) — otherwise they'd corrupt a freshly loaded tree.
    const saveLoadToken = loadTokenRef.current;
    const payload = buildChangeset(state.serverTree, sentTree);
    if (payload.ops.length === 0) return { ok: true, noop: true };

    const opIndex = new Map();
    payload.ops.forEach((op) => opIndex.set(op.op_id, op));

    dispatch({ type: "SAVE_START" });
    try {
      const result = await postChangeset(payload);
      const applied = Array.isArray(result?.applied) ? result.applied : null;

      // Defend against malformed/partial responses: missing applied[], wrong
      // length, or per-entry shape that contradicts the op kind. Any of these
      // would silently corrupt the snapshot if we accepted them.
      if (!applied || applied.length !== payload.ops.length) {
        const message = `Server returned ${applied?.length ?? 0} applied entries for ${payload.ops.length} ops`;
        dispatch({ type: "SAVE_ERROR", error: { code: "malformed_response", message } });
        return { ok: false, error: { code: "malformed_response", message } };
      }
      const seenOpIds = new Set();
      for (let i = 0; i < applied.length; i += 1) {
        const entry = applied[i];
        const op = opIndex.get(entry?.op_id);
        if (!op) {
          const message = `Server returned unknown op_id "${entry?.op_id}"`;
          dispatch({ type: "SAVE_ERROR", error: { code: "malformed_response", message } });
          return { ok: false, error: { code: "malformed_response", message } };
        }
        if (seenOpIds.has(entry.op_id)) {
          const message = `Server returned duplicate op_id "${entry.op_id}" in applied[]`;
          dispatch({ type: "SAVE_ERROR", error: { code: "malformed_response", message } });
          return { ok: false, error: { code: "malformed_response", message } };
        }
        seenOpIds.add(entry.op_id);
        if (!entry.public_id) {
          const message = `Server applied entry for op_id "${entry.op_id}" is missing public_id`;
          dispatch({ type: "SAVE_ERROR", error: { code: "malformed_response", message } });
          return { ok: false, error: { code: "malformed_response", message } };
        }
        if ((op.op === "create" || op.op === "update") && !entry.version) {
          const message = `Server applied entry for ${op.op} op "${entry.op_id}" is missing version`;
          dispatch({ type: "SAVE_ERROR", error: { code: "malformed_response", message } });
          return { ok: false, error: { code: "malformed_response", message } };
        }
      }

      const tempToReal = {};
      const versionUpdates = {};
      const deleted = new Set();
      applied.forEach((entry) => {
        const op = opIndex.get(entry.op_id);
        if (op.op === "create") {
          tempToReal[op.client_ref] = entry.public_id;
          versionUpdates[entry.public_id] = entry.version;
        } else if (op.op === "update") {
          versionUpdates[entry.public_id] = entry.version;
        } else if (op.op === "delete") {
          deleted.add(entry.public_id);
        }
      });

      // The user may have reloaded (Discard / store switch) during the await —
      // dropping our reconciled state on a stale tree would corrupt the snapshot.
      if (saveLoadToken.cancelled) return { ok: false, cancelled: true };
      dispatch({ type: "SAVE_OK", tempToReal, versionUpdates, deleted, sentTree });
      return { ok: true };
    } catch (e) {
      const data = e?.response?.data;
      const apiError = data?.errors?.[0];

      // On stale_version, silently refetch the offending entity and per-field merge
      // (the reducer keeps fields the user touched, adopts server values elsewhere).
      if (apiError?.code === "stale_version") {
        const op = opIndex.get(apiError.op_id);
        const conflictId = op?.public_id;
        if (conflictId) {
          try {
            const fresh = await refetchEntity(conflictId);
            // Same gate as SAVE_OK: a reload during the await invalidates this cycle.
            if (saveLoadToken.cancelled) return { ok: false, error: apiError, cancelled: true };
            if (fresh) {
              dispatch({ type: "STALE_REFRESH", publicId: conflictId, freshNode: fresh });
              dispatch({
                type: "SAVE_ERROR",
                error: {
                  op_id: apiError.op_id,
                  publicId: conflictId,
                  code: "stale_version",
                  field: apiError.field,
                  message: apiError.message,
                  refreshed: true,
                },
              });
              // publicId lets the caller name the entity in its message: on a bulk save
              // the user has no way to guess which of N entities went stale.
              return { ok: false, error: apiError, refreshed: true, publicId: conflictId };
            }
          } catch (refetchErr) {
            // fall through to generic error
          }
        }
      }

      if (saveLoadToken.cancelled) return { ok: false, cancelled: true };
      if (apiError) {
        const op = opIndex.get(apiError.op_id);
        dispatch({
          type: "SAVE_ERROR",
          error: {
            op_id: apiError.op_id,
            publicId: op?.public_id || op?.client_ref || null,
            code: apiError.code,
            field: apiError.field,
            message: apiError.message,
          },
        });
        return { ok: false, error: apiError };
      }

      dispatch({
        type: "SAVE_ERROR",
        error: { code: "unknown", message: e?.message || "Save failed" },
      });
      return { ok: false, error: e };
    }
  }, [state.serverTree, state.tree, refetchEntity]);

  return { ...state, dispatch, reset: load, saveAll };
}
